import React from "react";
import { Link, useParams } from "react-router-dom";
import { BLOG_POSTS } from "../data/blogData";
import SEO from "../components/SEO";

const BlogPost = () => {
  const { slug } = useParams();
  const post = BLOG_POSTS.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-[#1E3A5F] mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-[#CBA365] hover:underline font-sans">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = BLOG_POSTS
    .filter(p => p.id !== post.id && p.category === post.category)
    .slice(0, 2);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SEO
        title={post.title}
        description={post.excerpt}
        keywords={post.tags.join(", ")}
        canonicalUrl={`https://theofficemealco.com/blog/${post.slug}`}
        ogImage={post.image}
        ogType="article"
      />
      {/* Breadcrumb */}
      <div className="bg-[#F3EFE6] py-4 px-6 md:px-12 lg:px-20 border-b border-[#E8DDCB]">
        <div className="max-w-4xl mx-auto flex items-center text-sm text-[#64748B] font-sans">
          <Link to="/" className="hover:text-[#1E3A5F] transition-colors">Home</Link>
          <span className="mx-2 text-[#CBA365]">/</span>
          <Link to="/blog" className="hover:text-[#1E3A5F] transition-colors">Blog</Link>
          <span className="mx-2 text-[#CBA365]">/</span>
          <span className="text-[#1E3A5F] font-medium truncate">{post.title}</span>
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative h-[300px] md:h-[450px] lg:h-[500px]">
        <img 
          src={post.image} 
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F]/60 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:px-20">
          <div className="max-w-4xl mx-auto">
            <span className="bg-[#CBA365] text-[#1E3A5F] text-xs uppercase tracking-[0.22em] px-3 py-1.5 font-medium mb-4 inline-block">
              {post.category}
            </span>
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#FDFBF7] mb-4">
              {post.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-[#E8DDCB] font-sans">
              <span>{post.author}</span>
              <span className="w-1 h-1 bg-[#CBA365] rounded-full"></span>
              <span>{post.date}</span>
              <span className="w-1 h-1 bg-[#CBA365] rounded-full"></span>
              <span>{post.readTime}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="py-12 md:py-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto">
          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-8">
            {post.tags.map(tag => (
              <span 
                key={tag}
                className="text-xs text-[#64748B] bg-[#F3EFE6] px-3 py-1 font-sans"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Article Body */}
          <div 
            className="prose prose-lg max-w-none font-sans text-[#1E3A5F] leading-relaxed"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-[#E8DDCB]">
            <h4 className="font-serif text-lg text-[#1E3A5F] mb-4">Share this article</h4>
            <div className="flex gap-3">
              <a 
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#1E3A5F] text-[#FDFBF7] flex items-center justify-center hover:bg-[#CBA365] hover:text-[#1E3A5F] transition-colors"
              >
                X
              </a>
              <a 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#1E3A5F] text-[#FDFBF7] flex items-center justify-center hover:bg-[#CBA365] hover:text-[#1E3A5F] transition-colors"
              >
                in
              </a>
              <a 
                href={`https://wa.me/?text=${encodeURIComponent(post.title + ' ' + window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#1E3A5F] text-[#FDFBF7] flex items-center justify-center hover:bg-[#CBA365] hover:text-[#1E3A5F] transition-colors"
              >
                WA
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Commercial service links: helps readers and search engines move from advice to relevant services */}
      <section className="py-12 px-6 md:px-12 lg:px-20 bg-white border-t border-[#E8DDCB]">
        <div className="max-w-5xl mx-auto">
          <h3 className="font-serif text-2xl text-[#1E3A5F] mb-6">Explore our workplace food services in Bangalore</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/daily-corporate-meals" className="p-5 bg-[#F3EFE6] text-[#1E3A5F] hover:bg-[#E8DDCB]">Daily Corporate Meals</Link>
            <Link to="/event-catering" className="p-5 bg-[#F3EFE6] text-[#1E3A5F] hover:bg-[#E8DDCB]">Corporate Event Catering</Link>
            <Link to="/bento-boxes" className="p-5 bg-[#F3EFE6] text-[#1E3A5F] hover:bg-[#E8DDCB]">Corporate Meal Boxes</Link>
            <Link to="/pantry-management" className="p-5 bg-[#F3EFE6] text-[#1E3A5F] hover:bg-[#E8DDCB]">Office Pantry Management</Link>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 px-6 md:px-12 lg:px-20 bg-[#F3EFE6]">
          <div className="max-w-7xl mx-auto">
            <h3 className="font-serif text-2xl text-[#1E3A5F] mb-8">Related Articles</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {relatedPosts.map(related => (
                <Link 
                  key={related.id}
                  to={`/blog/${related.slug}`}
                  className="group bg-white shadow-sm hover:shadow-md transition-shadow flex gap-6"
                >
                  <div className="w-1/3 min-h-[150px] overflow-hidden">
                    <img 
                      src={related.image} 
                      alt={related.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="w-2/3 p-6 flex flex-col justify-center">
                    <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-2 block font-sans">
                      {related.category}
                    </span>
                    <h4 className="font-serif text-lg text-[#1E3A5F] group-hover:text-[#CBA365] transition-colors">
                      {related.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 lg:px-20 bg-[#1E3A5F]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl text-[#FDFBF7] mb-4">
            Ready to upgrade your office meals?
          </h2>
          <p className="text-[#E8DDCB] mb-8 font-sans">
            Let's discuss how we can transform your corporate dining experience.
          </p>
          <Link 
            to="/#contact" 
            className="inline-flex items-center gap-2 bg-[#CBA365] text-[#1E3A5F] px-8 py-4 font-medium hover:bg-[#E8DDCB] transition-colors font-sans"
          >
            Request a Quote
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
